import type { LucideIcon } from 'lucide-react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/cn';

type Tone = 'neutral' | 'brand' | 'warn' | 'danger';

const TONE_ACCENT: Record<Tone, { icon: string; ring: string }> = {
  neutral: { icon: 'bg-surface-sunk text-ink-muted', ring: 'ring-rule' },
  brand: { icon: 'bg-brand-50 text-brand-700', ring: 'ring-brand-100' },
  warn: { icon: 'bg-warn-soft text-warn-bold', ring: 'ring-warn-border' },
  danger: { icon: 'bg-danger-soft text-danger-bold', ring: 'ring-danger-border' },
};

export function StatTile({
  label,
  value,
  hint,
  footnote: _footnote,
  tone = 'neutral',
  icon: Icon,
  delta,
  className,
  style,
}: {
  label: string;
  value: string;
  hint?: string;
  footnote?: string;
  tone?: Tone;
  icon?: LucideIcon;
  delta?: { value: string; positive: boolean };
  className?: string;
  style?: React.CSSProperties;
}) {
  const accent = TONE_ACCENT[tone];
  return (
    <div
      className={cn(
        'card relative flex flex-col gap-3 p-5 transition-all hover:shadow-card-hover',
        className
      )}
      style={style}
    >
      <div className="flex items-start justify-between">
        <div className="text-[0.8125rem] font-medium text-ink-muted">{label}</div>
        {Icon && (
          <div
            className={cn(
              'grid h-9 w-9 place-items-center rounded-lg ring-1 ring-inset',
              accent.icon,
              accent.ring
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-[1.875rem] font-bold tracking-head text-ink tabular-nums">{value}</span>
        {delta && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-[0.75rem] font-semibold',
              delta.positive ? 'text-success-bold' : 'text-danger-bold'
            )}
          >
            {delta.positive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {delta.value}
          </span>
        )}
      </div>
      {hint && <div className="text-[0.8125rem] text-ink-muted">{hint}</div>}
    </div>
  );
}
